/* eslint-disable semi */
import Actions from 'reducers/Auth'
import ProfileActions from 'reducers/Profile'
import { thunks as MarketThunks } from 'reducers/Market'
import { thunks as PlaylistThunks } from 'reducers/PlayList'
import { AuthService, ProfileService } from 'services'

import jwt_decode from 'jwt-decode'
import {
  identifyUser,
  trackEvent,
} from 'utils/segmentService'
import {
  forceLogout,
  isSessionInvalidResponse,
  resetSessionState,
} from 'utils/authSession'
import type { AppDispatch } from 'types/store'

export const requestLoginEmail =
  ({ publicdevice, email }: { publicdevice: boolean; email: string }) =>
  (dispatch: AppDispatch) => {
    dispatch(Actions.setLoading(true))
    AuthService.requestLoginEmail({ publicdevice, email })
      .then(() => {
        dispatch(Actions.setLoading(false))
        dispatch(Actions.sendEmailSuccess())
      })
      .catch((error: any) => {
        const message = error.data.error
        dispatch(Actions.setLoading(false))
        dispatch(Actions.loginFail(message))
      })
  }

export const requestForgotPwd =
  ({ email }: { email: string }) =>
  (dispatch: AppDispatch) => {
    dispatch(Actions.setLoading(true))
    AuthService.requestForgotPwd({ email })
      .then(() => {
        dispatch(Actions.setLoading(false))
        dispatch(Actions.sendEmailSuccess())
      })
      .catch((error: any) => {
        const message = error.data.error
        dispatch(Actions.setLoading(false))
        dispatch(Actions.loginFail(message))
      })
  }

export const resetPassword =
  ({ password, token }: { password: string; token: string }) =>
  async (dispatch: AppDispatch) => {
    dispatch(Actions.setLoading(true))
    try {
      await AuthService.resetPassword({ password, token })
      dispatch(Actions.setLoading(false))
      dispatch(Actions.sendEmailSuccess())
    } catch (error: any) {
      const message = error.data?.error || error.message
      dispatch(Actions.setLoading(false))
      dispatch(Actions.loginFail(message))
    }
  }

export const loginPwdLess =
  ({
    token,
    successCallback = () => {},
    errorCallback = () => {},
  }: {
    token: string
    successCallback?: () => void
    errorCallback?: () => void
  }) =>
  (dispatch: AppDispatch) => {
    dispatch(Actions.setLoading(true))
    AuthService.loginPwdLess({ token })
      .then((response: any) => {
        dispatch(Actions.setLoading(false))
        const accessToken = response.data['access-token']
        const refreshToken = response.data['refresh-token']
        if (!!accessToken && !!refreshToken) {
          localStorage.setItem('accessToken', accessToken)
          localStorage.setItem('refreshToken', refreshToken)
          dispatch(Actions.loginSuccess(accessToken, refreshToken))
          if (successCallback) successCallback()
        } else if (errorCallback) {
          errorCallback()
        }
      })
      .catch((error: any) => {
        dispatch(Actions.setLoading(false))
        dispatch(Actions.loginFail(error.data.error))
        if (errorCallback) errorCallback()
      })
  }

export const loginWithPassword =
  ({
    email,
    password,
    publicDevice,
  }: {
    email: string
    password: string
    publicDevice: boolean
  }) =>
  (dispatch: AppDispatch) => {
    return new Promise((resolve, reject) => {
      dispatch(Actions.setLoading(true))
      AuthService.login({
        username: email,
        password: password,
        publicdevice: publicDevice,
      })
        .then((response: any) => {
          dispatch(Actions.setLoading(false))
          const accessToken = response.data['access-token']
          const refreshToken = response.data['refresh-token']
          const decodedToken: any = jwt_decode(accessToken)

          if (!!accessToken && !!refreshToken) {
            localStorage.setItem('accessToken', accessToken)
            localStorage.setItem('refreshToken', refreshToken)
            dispatch(Actions.loginSuccess(accessToken, refreshToken))

            ProfileService.getProfile().then((profileResponse: any) => {
              dispatch(ProfileActions.didGetProfile(profileResponse.data))
              dispatch(PlaylistThunks.listAll() as any)
            })
          }

          identifyUser(decodedToken.playerid, {
            email: decodedToken.email,
            name: decodedToken.fullname,
            username: decodedToken.username,
          })

          trackEvent('Login', {
            email: decodedToken.email,
            name: decodedToken.fullname,
            username: decodedToken.username,
          })

          resolve(response)
        })
        .catch((error: any) => {
          const message = error && error.data && error.data.error
          dispatch(Actions.setLoading(false))
          dispatch(Actions.loginFail(message))
          reject(error)
        })
    })
  }

export const refreshToken = () => async (dispatch: AppDispatch) => {
  dispatch(Actions.setLoading(true))
  const currentRefreshToken = localStorage.getItem('refreshToken')
  if (currentRefreshToken) {
    try {
      const response: any = await AuthService.refresh(currentRefreshToken)
      const accessToken = response.data['access-token']
      const newRefreshToken = response.data['refresh-token']

      if (!!accessToken && !!newRefreshToken) {
        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', newRefreshToken)
        dispatch(Actions.loginSuccess(accessToken, newRefreshToken))
        return true
      }

      dispatch(Actions.setLoading(false))
      await forceLogout(dispatch)
      return false
    } catch (e: any) {
      dispatch(Actions.setLoading(false))
      console.warn('refreshing token failed')

      if (isSessionInvalidResponse(e)) {
        await forceLogout(dispatch)
      }

      return false
    }
  }

  dispatch(Actions.setLoading(false))
  console.warn(
    'cannot refresh token because there is no existing refreshtoken'
  )
  return false
}

export const logout = () => (dispatch: AppDispatch) => {
  trackEvent('Logout', {})
  resetSessionState(dispatch)
  Promise.resolve(AuthService.logout()).catch(() => {})
}

export const cancelAllSessions = () => async (dispatch: AppDispatch) => {
  try {
    await AuthService.cancelAllSessions()
    trackEvent('Logout', {})
    await resetSessionState(dispatch)
  } catch (e: any) {
    console.warn(e)
  }
}

export const initialSetup =
  ({ token, navigate }: { token: string; navigate: (path: string) => void }) =>
  (dispatch: AppDispatch) => {
    if (token) {
      dispatch(logout() as any)
      dispatch(
        loginPwdLess({
          token,
          successCallback: () => {
            ProfileService.getProfile().then((response: any) => {
              dispatch(ProfileActions.didGetProfile(response.data))
              dispatch(MarketThunks.getMarkets() as any)
              dispatch(PlaylistThunks.listAll() as any)
            })
            navigate('/releases')
          },
        }) as any
      )
    }
  }

export const checkAccountByEmail = (email: string) => async () => {
  return AuthService.checkAccountsByEmail({ email })
}

export const sendEmailVerificationCode =
  (email: string) => async (dispatch: AppDispatch) => {
    dispatch(Actions.setLoading(true))
    try {
      await AuthService.sendEmailVerificationCode({ email })
      dispatch(Actions.setLoading(false))
      dispatch(Actions.sendEmailVerificationSuccess())
    } catch (error: any) {
      const message =
        error.response?.data?.error || 'Failed to send verification code'
      dispatch(Actions.setLoading(false))
      dispatch(Actions.sendEmailVerificationFail(message))
    }
  }

export const validateEmailVerificationCode = (data: any) => async () => {
  return AuthService.validateEmailVerificationCode(data)
}

export const confirmSignup = (data: any) => async () => {
  return AuthService.confirmSignup(data)
}

export const acceptInvitation =
  ({ invitation_token }: { invitation_token: string }) =>
  async () => {
    return AuthService.acceptInvitation({ invitation_token })
  }

export const processAndRetrieveConnections = () => async () => {
  return AuthService.processConnections()
}

export const sendInvitations =
  ({ invitations }: { invitations: any[] }) =>
  async (dispatch: AppDispatch) => {
    dispatch(Actions.sendInvitationsRequest(true))
    try {
      const response = await AuthService.sendInvitations({ invitations })
      dispatch(Actions.sendInvitationsSuccess(response))
      return response
    } catch (error) {
      dispatch(Actions.sendInvitationsFailure(error))
      throw error
    } finally {
      dispatch(Actions.setLoading(false))
    }
  }

export const getConnections = () => (dispatch: AppDispatch) => {
  dispatch(Actions.loadingConnections())

  AuthService.getConnections()
    .then((response: any) => {
      dispatch(Actions.loadingConnectionsSuccess(response.data))
    })
    .catch((error: any) => {
      dispatch(Actions.loadingConnectionsFailure(error))
    })
}

export const deleteConnection =
  (connectedPlayerId: string) => async (dispatch: AppDispatch) => {
    dispatch(Actions.setLoading(true))

    try {
      await AuthService.deleteConnection({
        connected_player_id: connectedPlayerId,
      })
      dispatch(Actions.setLoading(false))
      dispatch(Actions.deleteConnectionSuccess(connectedPlayerId))
      dispatch(Actions.loadingConnections())
    } catch (error: any) {
      dispatch(Actions.setLoading(false))
      dispatch(
        Actions.deleteConnectionFail(
          error?.response?.data?.error || 'Failed to delete connection'
        )
      )
    }
  }

export default {
  requestLoginEmail,
  requestForgotPwd,
  resetPassword,
  loginPwdLess,
  loginWithPassword,
  initialSetup,
  logout,
  refreshToken,
  cancelAllSessions,
  checkAccountByEmail,
  sendEmailVerificationCode,
  validateEmailVerificationCode,
  confirmSignup,
  sendInvitations,
  acceptInvitation,
  getConnections,
  deleteConnection,
  processAndRetrieveConnections,
}
